import './style.scss';

import { type IReconTarget, useReconciliation } from '../../../hooks/useReconciliation';
import { type ReconLevel, type ReconVerdict } from '../../../interface/reconciliation';
import { signed } from '../../utils/number';
import { type IChildScope } from '../overview/quick-analytics/utils';
import { ReconcilingState } from './ReconcilingState';

// A childScope describes an entity's CHILDREN (one rung below). To reconcile the
// entity itself we need its OWN level + id: the child level maps up one rung, and
// the entity id is the single scope value.
const GRANDCHILD_TO_PARENT: Record<string, { level: ReconLevel; scopeKey: 'wardId' | 'lgaId' | 'stateId' }> = {
	ec8a: { level: 'EC8B', scopeKey: 'wardId' },
	ec8b: { level: 'EC8C', scopeKey: 'lgaId' },
	ec8c: { level: 'EC8D', scopeKey: 'stateId' },
};

/** Build a reconciliation target for the drilled entity from its children scope. */
export const reconTargetFromChildScope = (
	childScope: IChildScope | null | undefined,
	entityCode: string,
	electionId?: number
): IReconTarget | null => {
	if (!childScope || !entityCode || !electionId) return null;
	const map = GRANDCHILD_TO_PARENT[childScope.resultType?.toLowerCase()];
	if (!map) return null;
	const entityId = childScope.scope[map.scopeKey];
	if (!entityId) return null;
	return { electionId, level: map.level, entityCode, entityId, entityName: entityCode };
};

const VERDICT: Record<ReconVerdict, { label: string; tone: string }> = {
	reconciled: { label: 'Reconciled', tone: 'ok' },
	minor: { label: 'Minor variance', tone: 'minor' },
	critical: { label: 'Doesn’t reconcile', tone: 'critical' },
	awaitingCoverage: { label: 'Awaiting coverage', tone: 'locked' },
};

/**
 * Compact, self-contained reconciliation summary for the Agent Coverage drawer,
 * shown when a parent agent drills into a NON-LEAF child (a ward/LGA that has its
 * own units). It answers "does this entity's own result reconcile with its units?"
 * at a glance. No navigation — the full page is scoped to the logged-in agent's
 * own entity, not an arbitrary child.
 */
export const ReconciliationDrawerStrip = ({ target }: { target: IReconTarget | null }) => {
	const { status, result } = useReconciliation(target);

	if (!target) return null;

	if (status === 'loading' || status === 'idle') {
		return <ReconcilingState variant='strip' />;
	}
	if (status === 'error' || !result) return null;

	const copy = VERDICT[result.verdict];
	const { childrenUploaded, childrenTotal } = result.completeness;
	const remaining = Math.max(childrenTotal - childrenUploaded, 0);

	let detail: string;
	if (result.verdict === 'awaitingCoverage') {
		detail = remaining > 0 ? `${remaining} unit${remaining === 1 ? '' : 's'} still to come in` : 'Waiting for the result';
	} else if (result.verdict === 'reconciled') {
		detail = `Matches all ${childrenTotal} units`;
	} else if (result.winnerFlip.flipped) {
		detail = `Winner flip · ${result.winnerFlip.aLeaderName} vs ${result.winnerFlip.bLeaderName}`;
	} else {
		const topParty = result.partyDeltas.find((d) => d.severity !== 'reconciled');
		const topTurnout = result.turnoutDeltas.find((d) => d.severity !== 'reconciled');
		if (topParty) detail = `${topParty.partyName} ${signed(topParty.delta)} across units`;
		else if (topTurnout) detail = `${topTurnout.label} ${signed(topTurnout.delta)}`;
		else detail = 'Differences found';
	}

	return (
		<div className={`rc-strip rc-strip--${copy.tone}`}>
			<span className='rc-strip__badge'>{copy.label}</span>
			<span className='rc-strip__detail'>{detail}</span>
			<span className='rc-strip__coverage'>
				{childrenUploaded}/{childrenTotal}
			</span>
		</div>
	);
};
